





















































































































import { Component, Emit, InjectReactive, PropSync, Vue, Watch } from 'vue-property-decorator';
import {
	FeedCorrection,
	FeedCorrectionInput,
	FeedCorrectionType,
	FeedTank,
	FeedTypeLevelMetricEnum,
} from '@/api';
import { CorrectionService } from '@/services/correction-service';
import Toast from '@/components/display/toast.vue';
import { FeedTankService } from '@/pages/account/products/feed/services';
import { AdditionalFeedParams, CustomFormulaResult } from '@/typings/custom-formula-calculator';

@Component({
	components: {},
})
export default class FeedTankCorrectionAdd extends Vue {
	public readonly smallScreen = window.innerWidth;
	private readonly toast = new Toast();

	public refillRawDataFields: { key: string; label: string }[] = [];

	@InjectReactive('selectedFeedTank')
	public feedTank!: FeedTank;

	@PropSync('savedCorrections')
	public _savedCorrections!: FeedCorrection[];

	@PropSync('parameters')
	public _parameters!: Record<string, string | number>;

	@PropSync('correction')
	public _correction!: Record<string, number | null>;

	public addCorrectionForm = { notes: '', amount: '', correctionType: 'refill' };
	public series = 'volume';

	public rawData: any[] = [];
	public checkedRawData: any[] = [];

	private sortNext?: string;
	private sortPrev?: string;
	public loading: boolean = false;
	public addingCorrection: boolean = false;

	private feedTankService: FeedTankService | null = null;

	public get state(): boolean | null {
		return this.series === 'volume'
			? this.checkedRawData.length === 1
			: this.checkedRawData.length >= 1;
	}

	@Watch('feedTank')
	public async created(): Promise<void> {
		this.refillRawDataFields = [
			{ key: 'sel', label: '' },
			{ key: 'date', label: 'Refill Date' },
			{ key: 'data.refillVolume', label: `Refill ${this.isTonnes() ? 'Weight' : 'Volume'}` },
			{ key: 'data.startLevel.volume', label: `Start ${this.isTonnes() ? 'Weight' : 'Volume'}` },
			{ key: 'data.endLevel.volume', label: `End ${this.isTonnes() ? 'Weight' : 'Volume'}` },
		];

		this.feedTankService = new FeedTankService(this.feedTank);
		await this.setRawData();
	}

	public unit(): string {
		return this.isTonnes() ? 't' : 'L';
	}

	public unitLabel(): string {
		return this.isTonnes() ? 'Tonnes' : 'Litres';
	}

	public isTonnes(): boolean {
		return this.feedTank.feedtype?.levelMetric == FeedTypeLevelMetricEnum.Weight;
	}

	public rowDataRowClass(item: any): string {
		return item != undefined &&
			item.data != undefined &&
			item.data.faultName != undefined &&
			item.data.faultName.length > 1
			? 'table-secondary'
			: '';
	}

	public async setRawData(): Promise<void> {
		this.checkedRawData = [];
		this.addCorrectionForm.amount = '';
		await this.reloadRawData();
	}

	private async reloadRawData(sortKey?: string, prev: boolean = false): Promise<void> {
		const data = await this.feedTankService?.getTankRawData(
			this.feedTank,
			'logical-refill',
			sortKey,
			prev,
		);
		this.sortNext = data?.data?.links?.nextSort;
		this.sortPrev = data?.data?.links?.currentSortKey;

		this.rawData = data?.data?.data ?? [];
	}

	public async back(): Promise<void> {
		if (this.sortPrev != null) await this.reloadRawData(this.sortPrev, true);
	}

	public async next(): Promise<void> {
		if (this.sortNext != null) await this.reloadRawData(this.sortNext);
	}

	public async clear(): Promise<void> {
		this.checkedRawData = [];
	}

	public async addCorrection(): Promise<void> {
		this.addingCorrection = true;
		const rawData = this.checkedRawData[0];
		const correctionInput: FeedCorrectionInput = {
			feedTypeId: this.feedTank.feedTypeId ?? 0,
			correctionType: FeedCorrectionType.Refill,
			eventTimestamp: rawData.data.timestamp,
			startReading: rawData.data.startLevel.sensorVal,
			endReading: rawData.data.endLevel.sensorVal,
			volume: this.isTonnes() ? 0 : +this.addCorrectionForm?.amount ?? 0,
			weight: !this.isTonnes() ? 0 : (+this.addCorrectionForm?.amount ?? 0) * 1000,
			originalVolume: rawData.data.refillVolume,
			originalWeight: Math.round(
				rawData.data.refillVolume * (this.feedTank?.feedtype?.density ?? 1),
			),
			notes: this.addCorrectionForm.notes,
			additionalParameters: {
				start: rawData.data.startLevel.additionalParameters,
				end: rawData.data.endLevel.additionalParameters,
			},
		};

		if (this.feedTank?.id) {
			const { error, correction } = await CorrectionService.addCorrection(
				this.feedTank.id,
				correctionInput,
			);
			if (error != null || !correction)
				this.toast.makeToast(
					error ?? 'Unknown error, refresh and make sure the correction was added',
					'Error',
					'danger',
				);
			else {
				this.toast.makeToast('Refill correction added', 'Success', 'success');
				this.addCorrectionForm = {
					amount: '',
					correctionType: FeedCorrectionType.Refill,
					notes: '',
				};
				this.checkedRawData = [];
				this.onAddedCorrection();
			}
		}
		this.addingCorrection = false;
	}

	@Emit('addedCorrection')
	public onAddedCorrection(): void {}
}
